/* eslint-disable react/jsx-boolean-value */
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Paper from "@mui/material/Paper";
import { useParams, useNavigate, Link } from "react-router-dom";
import OrderInformation from "pages/order/OrderInformation";
import OrderHistory from "pages/order/OrderHistory";
import Form from "pages/order/Form";
import PageTitle from "components/PageTitle";
import AlertDialog from "components/AlertDialog";
import Button from "components/LoadingButton";
import Snackbar from "components/SnackBar";
import { useFetch } from "hooks/useFetch";
import { usePost } from "hooks/usePost";
import { CSR_ROLE, DEVELOPER_ROLE } from "constants/roles";
import { setAppVersion } from "features/appVersion/appVersionSlice";
import OrderCheckInDialog from "components/OrderCard/OrderCheckInDialog";
import OrderComments from "components/OrderCard/OrderComments";
import {
  CONFLUENCE_VENDORS,
  CONFLUENCE_ORDERS,
  CONFLUENCE_NOTIFICATIONS,
  PRINT_PROVIDERS,
  APP_VERSION,
} from "constants/services";
import { ORDERS } from "constants/routes";
import OfflineBoltIcon from "@mui/icons-material/OfflineBolt";
import { cancelled, inProduction, ready, defined } from "pages/orders/data";
import {
  StyledContainer,
  StyledHeader,
  StyledRushOrder,
  StyledCheckInButton,
  StyledRow,
  StyledCheckInsWrapper,
} from "styles/pages/order";
import DecoratorWorkOrderDialog from "pages/orders/DecoratorWorkOrderDialog";

const CreateOrder = () => {
  const [orderCheckInDialog, toggleOrderCheckInDialog] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [state, setState] = useState();
  const { orderNumber, vendorName } = useParams();
  const { mutateAsync } = usePost();
  const appVersion = useSelector((s) => s?.appVersion?.version);
  const user = useSelector((s) => s?.user);
  const { mutateAsync: mutateAsyncConfluenceNotification } = usePost();

  const { refetch } = useFetch(CONFLUENCE_VENDORS, CONFLUENCE_VENDORS, {
    enabled: false,
  });

  const { refetch: appVersionRefetch } = useFetch(APP_VERSION, APP_VERSION, {
    enabled: false,
  });

  const compareVersion = async () => {
    const { data } = await appVersionRefetch();
    if (data && data?.CurrentAppVersion !== appVersion) {
      dispatch(setAppVersion({ version: data?.CurrentAppVersion }));
      window.location.reload();
    }
  };

  useEffect(() => {
    compareVersion();
    return () => true;
  }, [appVersion]);

  useEffect(() => setState(), [navigate]);

  const { refetch: refetchOrder } = useFetch(
    CONFLUENCE_ORDERS,
    `${CONFLUENCE_ORDERS}?orderNumber=${orderNumber}&vendorName=${vendorName}`,
    {
      enabled: false,
    }
  );

  const { refetch: refetchPrintProviders } = useFetch(
    PRINT_PROVIDERS,
    PRINT_PROVIDERS,
    {
      enabled: false,
    }
  );

  const fetchPrintProviders = async () => {
    const { data } = await refetchPrintProviders();

    if (data) {
      setState({
        ...state,
        printProviders: data,
      });
    }
  };

  const fetchVendors = async () => {
    const { data } = await refetch();

    if (data) {
      setState({
        ...state,
        vendors: data,
      });
    }
  };

  const initiateState = async (data) => {
    if (data) {
      const p = {
        ...state,
        ...data,
        vendor: { id: data?.vendorName, label: data?.vendorName },
        vendorNameInput: data?.vendorName,
        printProvider: { id: data?.printProvider, label: data?.printProvider },
        printProviderInput: data?.printProvider,
        poNumber: orderNumber,
        assigneeEmails: data?.assigneeEmails?.map((c) => ({
          title: c,
          value: c,
        })),
        clientEmails: data?.clientEmails?.map((c) => ({
          title: c,
          value: c,
        })),
        decorations: data?.decorations?.map((c) => ({
          title: c,
          value: c,
        })),
      };

      for (let i = 0; i < data?.decorations?.length; i += 1) {
        const title = data?.decorations[i];
        const completedDateKey = `${title?.toLowerCase()}CompletedDate`;
        const inProductionDisableKey = `${title?.toLowerCase()}DisableInProductionDate`;
        p[inProductionDisableKey] = !!data[completedDateKey];
      }

      setState(p);
    }
  };

  const fetchOrder = async () => {
    const { data } = await refetchOrder();
    initiateState(data);
  };

  const fetchOrderData = () => {
    if (orderNumber && vendorName) {
      fetchOrder();
    }
  };

  const updateOrder = async (payload) => {
    const { data } = await mutateAsync({
      url: CONFLUENCE_ORDERS,
      isPatch: true,
      data: payload,
    });

    initiateState(data);
  };

  useEffect(() => {
    fetchOrderData();
  }, [orderNumber, vendorName]);

  useEffect(() => {
    if (!state?.vendors) fetchVendors();
  }, [state]);

  useEffect(() => {
    if (!state?.printProviders) fetchPrintProviders();
  }, [state]);

  const getOrderDates = () => {
    const payload = {};

    for (let i = 0; i < state?.decorations?.length; i += 1) {
      const element = state?.decorations[i];
      const inProductionKey = `${element?.title?.toLowerCase()}InProductionDate`;
      const completedKey = `${element?.title?.toLowerCase()}CompletedDate`;
      payload[inProductionKey] = state[inProductionKey];
      payload[completedKey] = state[completedKey];
    }
    return payload;
  };

  const isOrderDefined = state?.status === defined?.value;
  const isOrderInPreProd = state?.status === ready?.value;
  const isOrderInProduction = state?.status === inProduction?.value;

  return (
    <StyledContainer>
      <Breadcrumbs sx={{ marginBottom: "10px" }}>
        <Link underline="hover" color="inherit" to={ORDERS}>
          Orders
        </Link>
        <Typography color="text.primary">Order</Typography>
      </Breadcrumbs>

      <StyledHeader>
        <PageTitle
          title={
            orderNumber
              ? `Edit Order${state?.status ? ` (${state?.status})` : ""}`
              : "Create Order"
          }
        />
        <div className="actions">
          {!!orderNumber && (
            <Button
              label="Cancel Order"
              variant="contained"
              onClick={() =>
                setState({ ...state, showCancelOrderDialog: true })
              }
            />
          )}

          {!!state?.estimateData?.invoice && (
            <Button
              label="Estimate Link"
              variant="contained"
              onClick={() =>
                window.open(state?.estimateData?.qbEstimateData?.Estimate?.Link)
              }
            />
          )}

          {(isOrderDefined || isOrderInPreProd || isOrderInProduction) &&
            (user?.role === DEVELOPER_ROLE.role ||
              user?.role === CSR_ROLE.role) &&
            !state?.rushOrder && (
              <StyledRushOrder
                onClick={async (e) => {
                  e.stopPropagation();
                  const p = {
                    orderNumber: state?.orderNumber,
                    vendorName: state?.vendorName,
                    rushOrder: true,
                  };

                  updateOrder(p);
                }}
              >
                <OfflineBoltIcon />
                Rush Order
              </StyledRushOrder>
            )}
          <StyledCheckInButton
            variant="contained"
            onClick={() => toggleOrderCheckInDialog(!orderCheckInDialog)}
          >
            Check-In
          </StyledCheckInButton>

          {state?.productionOrder && (
            <StyledCheckInButton
              variant="contained"
              onClick={() =>
                setState({
                  ...state,
                  order: { ...state?.order, createSalesOrder: true },
                })
              }
            >
              Edit Order
            </StyledCheckInButton>
          )}
        </div>
      </StyledHeader>

      <Form
        state={state}
        fetchOrder={fetchOrder}
        setState={setState}
        updateArtfiles={(artfiles) =>
          updateOrder({
            orderNumber: state?.poNumber,
            vendorName: state?.vendor?.label,
            artfiles,
          })
        }
        updateOrderStatus={(status) =>
          updateOrder({
            orderNumber: state?.poNumber,
            vendorName: state?.vendor?.label,
            status,
            requestedShipDate: state?.requestedShipDate,
            requestedInHandDate: state?.requestedInHandDate,
            expectedShipDate: state?.expectedShipDate,
            notifyClients: state?.notifyClients,
            packingSlipUrl: state?.packingSlipUrl,
            // invoiceUrl: state?.invoiceUrl,
            estimationUrl: state?.estimationUrl,

            artfiles: state?.artfiles,
            totalStitchCount: state?.totalStitchCount,
            totalPrintLocations: state?.totalPrintLocations,
            totalPeiceCount: state?.totalPeiceCount,
            ...getOrderDates(),
          })
        }
        createOrder={async () => {
          const payload = {
            orderNumber: state?.poNumber?.trim(),
            vendorName: state?.vendor?.label?.trim(),
            poSecureUrl: state?.poSecureUrl,
            requestedShipDate: state?.requestedShipDate,
            requestedInHandDate: state?.requestedInHandDate,
            expectedShipDate: state?.expectedShipDate,
            clientEmails: state?.clientEmails?.map((c) => c?.value),
            assigneeEmails: state?.assigneeEmails?.map((c) => c?.value),
            decorations: state?.decorations?.map((c) => c?.value),
            notifyClients: state?.notifyClients,
            printProvider: state?.printProvider?.label,
            artfiles: state?.artfiles,
            totalStitchCount: state?.totalStitchCount,
            totalPrintLocations: state?.totalPrintLocations,
            totalPeiceCount: state?.totalPeiceCount,
            preApprovedArtproofs: state?.preApprovedArtproofs,
            estimateData: state?.estimateData,
            ...getOrderDates(),
          };

          await compareVersion();
          const { data: response } = orderNumber
            ? await updateOrder(payload)
            : await mutateAsync({
                url: CONFLUENCE_ORDERS,
                data: payload,
                isPut: true,
              });

          if (response) {
            navigate(ORDERS);
          }
        }}
      />

      {orderNumber && (
        <OrderInformation
          state={state}
          setState={setState}
          sendNotification={async (payload) => {
            const { data } = await mutateAsyncConfluenceNotification({
              url: CONFLUENCE_NOTIFICATIONS,
              data: payload,
            });
            initiateState(data);
          }}
          onApprove={(payload) => updateOrder(payload)}
          updateOrder={(artProofUrl) =>
            updateOrder({
              orderNumber,
              vendorName,
              artfiles: state?.artfiles,
              artProof: {
                artProofUrl,
                decision: "pending",
                notes: "",
              },
            })
          }
          updatePOOrder={(poSecureUrl) =>
            updateOrder({
              orderNumber,
              vendorName,
              poSecureUrl,
            })
          }
        />
      )}
      {orderNumber && (
        <StyledRow>
          <Paper>
            <OrderHistory state={state} />
          </Paper>
          <Paper>
            <StyledCheckInsWrapper>
              <h3 className="title">Previous Check-Ins</h3>
              <OrderComments comments={state?.orderCheckInData} />
            </StyledCheckInsWrapper>
          </Paper>
        </StyledRow>
      )}
      {!!(state?.apiMessage && state?.apiSeverity) && (
        <Snackbar
          message={state?.apiMessage}
          onClose={() =>
            setState({ ...state, apiMessage: null, apiSeverity: null })
          }
          severity={state?.apiSeverity}
        />
      )}

      {state?.showCancelOrderDialog && (
        <AlertDialog
          open={state?.showCancelOrderDialog}
          title="Cancel Order"
          description="Are you sure, you want to cancel this order?"
          agreeTitle="Yes"
          disagreeTitle="No"
          onAgree={async () => {
            await updateOrder({
              orderNumber: state?.poNumber,
              vendorName: state?.vendor?.label,
              status: cancelled.value,
              notifyClients: state?.notifyClients,
              artfiles: state?.artfiles,
            });
          }}
          onDisagree={() =>
            setState({ ...state, showCancelOrderDialog: false })
          }
        />
      )}

      {orderCheckInDialog && (
        <OrderCheckInDialog
          open={orderCheckInDialog}
          setOpen={() => toggleOrderCheckInDialog(!orderCheckInDialog)}
          order={state}
          refresh={fetchOrder}
        />
      )}

      {state?.order?.createSalesOrder && (
        <DecoratorWorkOrderDialog
          state={{
            ...state?.order,
            vendorName: state?.vendorName,
            clientEmails: state?.clientEmails,
            assigneeEmails: state?.assigneeEmails,
            decorations: state?.decorations,
            printProvider: state?.printProvider,
            orderNumber: state?.poNumber,
          }}
          setState={(v) => setState({ ...state, order: v })}
          fetchOrder={fetchOrder}
        />
      )}
    </StyledContainer>
  );
};

export default CreateOrder;
